import { AnimatedValue, InterpolatedValue, Observable } from '../animated/AnimatedValue'
import { ScrollVideoOptionsFull } from '../types'
import loadImage from './loadImage'

const canUseWebP = (() => {
  const elem = document.createElement('canvas')

  if (!!(elem.getContext && elem.getContext('2d'))) {
    // was able or not to get WebP representation
    return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0
  }

  // very old browser like IE 8, canvas not supported
  return false
})()

const range = (length: number): number[] => Array.from(Array(length)).map((_, x) => x)

const videoCache = (
  args: AnimatedValue<ScrollVideoOptionsFull>,
  isHidden: Observable<boolean>
): { cache: AnimatedValue<HTMLImageElement[]>; ready: AnimatedValue<boolean> } => {
  const cache = new AnimatedValue<HTMLImageElement[]>([])
  const ready = new AnimatedValue<boolean>(false)

  new InterpolatedValue({ args, isHidden }, ({ args, isHidden }) => ({
    ...args,
    isHidden
  })).addListener(({ src, frames, webp, isHidden, autoplay }) => {
    let cancelled = false
    const extension = webp && canUseWebP ? '.webp' : '.jpg'
    cache.value = []
    ready.value = false
    if (!isHidden && src && frames) {
      const path = src.endsWith('/') ? src : src + '/'
      const filenamelength = frames.toString().length
      const filenames: [number, string][] = range(frames).map((i) => {
        return [i, path + String(i + 1).padStart(filenamelength, '0') + extension]
      })
      const filenameGroups: [number, string][][] = []
      if (autoplay) {
        filenameGroups.push(filenames)
      } else {
        const maxExp = 4
        for (let exp = maxExp; exp >= 0; exp--) {
          const factor = Math.pow(2, exp)
          const lastFactor = Math.pow(2, exp + 1)
          filenameGroups.push(filenames.filter((_filename, i) => !(i % factor) && (exp === maxExp || i % lastFactor)))
        }
      }
      ;(async (): Promise<void> => {
        for (let i = 0; i < filenameGroups.length; i++) {
          if (cancelled) {
            return
          }
          await Promise.all(
            filenameGroups[i].map(([index, filename]) => {
              return loadImage(filename)
                .then((image) => {
                  if (!cancelled) {
                    const nextCache = [...cache.value]
                    nextCache[index] = image
                    cache.value = nextCache
                  }
                })
                .catch(() => undefined)
            })
          )
        }
      })().then(() => {
        if (!cancelled) {
          ready.value = true
        }
      })
    }
    return () => {
      cancelled = true
    }
  }, true)

  return { cache, ready }
}

export default videoCache
